import { useCallback } from "react";
import { Map, AdvancedMarker } from "@vis.gl/react-google-maps";
import type { MapMouseEvent } from "@vis.gl/react-google-maps";
import type { ThemeClasses, MapPoint } from "../types";
import { useTranslation } from "react-i18next";

export interface PointOfInterestMapPreviewProps {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  mapCenter: MapPoint;
  markerPosition: MapPoint | null;
  onMarkerPositionChange: (position: MapPoint) => void;
  readOnly?: boolean;
}

export function PointOfInterestMapPreview({
  themeClasses,
  isDarkMode,
  mapCenter,
  markerPosition,
  onMarkerPositionChange,
  readOnly = false,
}: PointOfInterestMapPreviewProps) {
  const { t } = useTranslation("adminSections");

  const handleMapClick = useCallback(
    (event: MapMouseEvent) => {
      if (readOnly) return;
      
      const detail = event.detail;
      const lat = detail?.latLng?.lat;
      const lng = detail?.latLng?.lng;
      
      if (lat !== undefined && lng !== undefined) {
        onMarkerPositionChange({ lat, lng });
      }
    },
    [onMarkerPositionChange, readOnly]
  );

  const handleMarkerDragEnd = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (readOnly) return;
      
      const lat = event.latLng?.lat();
      const lng = event.latLng?.lng();
      
      if (lat !== undefined && lng !== undefined) {
        onMarkerPositionChange({ lat, lng });
      }
    },
    [onMarkerPositionChange, readOnly]
  );

  return (
    <div className="w-full h-full relative">
      <Map
        mapId={isDarkMode ? "dark-map-poi" : "light-map-poi"}
        defaultCenter={mapCenter}
        defaultZoom={14}
        gestureHandling="greedy"
        disableDefaultUI={false}
        zoomControl={true}
        mapTypeControl={false}
        streetViewControl={false}
        fullscreenControl={true}
        onClick={handleMapClick}
        style={{ width: "100%", height: "100%" }}
      >
        {markerPosition && (
          <AdvancedMarker
            position={markerPosition}
            draggable={!readOnly}
            onDragEnd={handleMarkerDragEnd}
          >
            <div className="flex flex-col items-center">
              <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                <i className="ri-map-pin-2-fill text-white text-lg"></i>
              </div>
              <div className="w-0 h-0 border-l-[6px] border-r-[6px] border-t-[8px] border-l-transparent border-r-transparent border-t-red-500 -mt-0.5"></div>
            </div>
          </AdvancedMarker>
        )}
      </Map>

      {/* Controles del mapa */}
      <div className="absolute top-2 right-2 flex flex-col gap-1">
        <button
          type="button"
          className={`w-8 h-8 rounded-md ${isDarkMode ? "bg-dark-card" : "bg-white"} shadow-md flex items-center justify-center ${themeClasses.text} hover:bg-primary-500 hover:text-white transition-colors`}
          title="Zoom in"
        >
          <i className="ri-add-line"></i>
        </button>
        <button
          type="button"
          className={`w-8 h-8 rounded-md ${isDarkMode ? "bg-dark-card" : "bg-white"} shadow-md flex items-center justify-center ${themeClasses.text} hover:bg-primary-500 hover:text-white transition-colors`}
          title="Zoom out"
        >
          <i className="ri-subtract-line"></i>
        </button>
        <button
          type="button"
          className={`w-8 h-8 rounded-md ${isDarkMode ? "bg-dark-card" : "bg-white"} shadow-md flex items-center justify-center ${themeClasses.text} hover:bg-primary-500 hover:text-white transition-colors`}
          title="Mi ubicación"
        >
          <i className="ri-focus-3-line"></i>
        </button>
      </div>

      {/* Mensaje cuando no hay marcador */}
      {!markerPosition && !readOnly && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className={`flex flex-col items-center gap-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
            <i className="ri-map-pin-add-line text-4xl opacity-50"></i>
            <p className="text-sm">{t("pointsOfInterest.form.clickToSelectLocation")}</p>
          </div>
        </div>
      )}
    </div>
  );
}
